import React, { FC, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { ChevronDown, ChevronUp, Edit2, Save, X } from 'lucide-react';
import { useInjuryEndpoints } from '../../hooks/useInjuryEndpoints';
import { ShowByRole } from '../../shared/ShowByRole';
import { IAnamnesis, IUpsertAnamnesisDto } from '../../models/anamnesis';

interface AnamnesisCardProps {
  athleteId: string;
}

/** Calcola il badge colore per l'ultima visita medica.
 *  Verde < 12 mesi, giallo 12-24 mesi, rosso > 24 mesi o vuoto. */
function getMedicalVisitBadge(dateStr?: string): { color: string; label: string } | null {
  if (!dateStr) return { color: '#ef4444', label: 'Non registrata' };
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffMonths = diffMs / (1000 * 60 * 60 * 24 * 30.44);
  if (diffMonths < 12) return { color: '#22c55e', label: 'Aggiornata' };
  if (diffMonths < 24) return { color: '#f59e0b', label: 'Da rinnovare' };
  return { color: '#ef4444', label: 'Scaduta' };
}

/** Calcola il badge colore per il certificato medico.
 *  Verde > 30gg, giallo ≤ 30gg, rosso scaduto o vuoto. */
function getCertificateBadge(dateStr?: string): { color: string; label: string } | null {
  if (!dateStr) return { color: '#ef4444', label: 'Non registrato' };
  const diffMs = new Date(dateStr).getTime() - Date.now();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  if (diffDays > 30) return { color: '#22c55e', label: 'Valido' };
  if (diffDays > 0) return { color: '#f59e0b', label: 'In scadenza' };
  return { color: '#ef4444', label: 'Scaduto' };
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('it-IT');
}

export const AnamnesisCard: FC<AnamnesisCardProps> = ({ athleteId }) => {
  const injuryEndpoints = useInjuryEndpoints();
  const queryClient = useQueryClient();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const { data: anamnesis, isLoading } = useQuery<IAnamnesis | null>({
    queryKey: ['anamnesis', athleteId],
    queryFn: async () => {
      const { data } = await injuryEndpoints.getAnamnesis(athleteId);
      return data;
    },
    enabled: !!athleteId,
  });

  const methods = useForm<IUpsertAnamnesisDto>({
    defaultValues: {
      generalNotes: anamnesis?.generalNotes ?? '',
      allergiesNotes: anamnesis?.allergiesNotes ?? '',
      psychologicalNotes: anamnesis?.psychologicalNotes ?? '',
      preExistingInjuries: anamnesis?.preExistingInjuries ?? '',
      physiotherapistNotes: anamnesis?.physiotherapistNotes ?? '',
      lastMedicalVisit: anamnesis?.lastMedicalVisit ?? '',
      medicalCertificateExpiry: anamnesis?.medicalCertificateExpiry ?? '',
      emergencyContactName: anamnesis?.emergencyContactName ?? '',
      emergencyContactPhone: anamnesis?.emergencyContactPhone ?? '',
    },
  });

  const { handleSubmit, register, reset, formState: { isSubmitting } } = methods;

  const startEdit = () => {
    reset({
      generalNotes: anamnesis?.generalNotes ?? '',
      allergiesNotes: anamnesis?.allergiesNotes ?? '',
      psychologicalNotes: anamnesis?.psychologicalNotes ?? '',
      preExistingInjuries: anamnesis?.preExistingInjuries ?? '',
      physiotherapistNotes: anamnesis?.physiotherapistNotes ?? '',
      lastMedicalVisit: anamnesis?.lastMedicalVisit ?? '',
      medicalCertificateExpiry: anamnesis?.medicalCertificateExpiry ?? '',
      emergencyContactName: anamnesis?.emergencyContactName ?? '',
      emergencyContactPhone: anamnesis?.emergencyContactPhone ?? '',
    });
    setIsExpanded(true);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    reset();
  };

  const onSubmit = async (values: IUpsertAnamnesisDto) => {
    try {
      await injuryEndpoints.putAnamnesis(athleteId, values);
      queryClient.invalidateQueries({ queryKey: ['anamnesis', athleteId] });
      toast.success('Anamnesi aggiornata');
      setIsEditing(false);
    } catch {
      toast.error('Errore salvataggio anamnesi');
    }
  };

  const cardStyle: React.CSSProperties = {
    background: 'var(--c-card-bg)',
    borderRadius: 12,
    border: '1px solid #e7e7e7',
    overflow: 'hidden',
    fontFamily: 'Satoshi, sans-serif',
    marginBottom: 16,
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    cursor: 'pointer',
    borderBottom: isExpanded ? '1px solid #e7e7e7' : 'none',
  };

  const fieldStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    marginBottom: 14,
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    color: 'var(--c-text-1)',
  };

  const valueStyle: React.CSSProperties = {
    fontSize: 13,
    color: 'var(--c-text-0)',
  };

  const emptyStyle: React.CSSProperties = {
    fontSize: 13,
    color: 'var(--c-text-3)',
    fontStyle: 'italic',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    fontFamily: 'Satoshi, sans-serif',
    fontSize: 13,
    outline: 'none',
    resize: 'vertical' as const,
    minHeight: 60,
  };

  const dateInputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    fontFamily: 'Satoshi, sans-serif',
    fontSize: 13,
    outline: 'none',
  };

  const textInputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    fontFamily: 'Satoshi, sans-serif',
    fontSize: 13,
    outline: 'none',
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    color: '#013344',
    marginBottom: 10,
    marginTop: 16,
    paddingBottom: 6,
    borderBottom: '1px solid #f0f0f0',
  };

  if (isLoading) {
    return (
      <div style={cardStyle}>
        <div style={{ padding: 16, textAlign: 'center', color: 'var(--c-text-3)', fontSize: 13 }}>
          Caricamento anamnesi...
        </div>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <div style={headerStyle} onClick={() => !isEditing && setIsExpanded((p) => !p)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--c-text-0)' }}>Storico clinico / Anamnesi</span>
          {!anamnesis && (
            <span style={{ fontSize: 11, color: 'var(--c-text-3)', background: 'var(--c-app-bg)', padding: '2px 8px', borderRadius: 9999 }}>
              nessun dato
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ShowByRole roles={['isAdmin', 'isDirector', 'isCoach']}>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); startEdit(); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '4px 10px', borderRadius: 6,
                border: '1px solid #e2e8f0', background: 'var(--c-card-bg)',
                fontSize: 12, fontWeight: 600, fontFamily: 'Satoshi, sans-serif',
                cursor: 'pointer', color: '#013344',
              }}
            >
              <Edit2 size={12} />
              Modifica
            </button>
          </ShowByRole>
          {isExpanded ? <ChevronUp size={16} color="#4d4d4d" /> : <ChevronDown size={16} color="#4d4d4d" />}
        </div>
      </div>

      {isExpanded && (
        <div style={{ padding: '16px 16px 8px' }}>
          {isEditing ? (
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                {/* Sezione anamnesi medica base */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Condizioni croniche / Note generali</label>
                    <textarea
                      style={inputStyle}
                      placeholder="es. asma, diabete, ipertensione..."
                      {...register('generalNotes')}
                    />
                  </div>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Allergie</label>
                    <textarea
                      style={inputStyle}
                      placeholder="es. lattice, FANS, pollini..."
                      {...register('allergiesNotes')}
                    />
                  </div>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Anamnesi familiare / Note psicologiche</label>
                    <textarea
                      style={inputStyle}
                      placeholder="Patologie rilevanti in famiglia..."
                      {...register('psychologicalNotes')}
                    />
                  </div>
                </div>

                {/* Sezione documenti medici */}
                <div style={sectionTitleStyle}>Documenti medici</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Ultima visita medica</label>
                    <input
                      type="date"
                      style={dateInputStyle}
                      {...register('lastMedicalVisit')}
                    />
                  </div>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Scadenza certificato medico</label>
                    <input
                      type="date"
                      style={dateInputStyle}
                      {...register('medicalCertificateExpiry')}
                    />
                  </div>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Contatto emergenza — Nome</label>
                    <input
                      type="text"
                      style={textInputStyle}
                      placeholder="Nome e cognome..."
                      {...register('emergencyContactName')}
                    />
                  </div>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Contatto emergenza — Telefono</label>
                    <input
                      type="text"
                      style={textInputStyle}
                      placeholder="+39 ..."
                      {...register('emergencyContactPhone')}
                    />
                  </div>
                </div>

                {/* Sezione note cliniche */}
                <div style={sectionTitleStyle}>Note cliniche</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Note fisioterapista</label>
                    <textarea
                      style={inputStyle}
                      placeholder="Note cliniche del fisioterapista..."
                      {...register('physiotherapistNotes')}
                    />
                  </div>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Infortuni pre-LEOPART</label>
                    <textarea
                      style={inputStyle}
                      placeholder="Infortuni rilevanti precedenti all'ingresso..."
                      {...register('preExistingInjuries')}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12, paddingTop: 12, borderTop: '1px solid #f6f6f6' }}>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      padding: '6px 14px', borderRadius: 8,
                      border: '1px solid #e2e8f0', background: 'var(--c-card-bg)',
                      fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      fontFamily: 'Satoshi, sans-serif',
                    }}
                  >
                    <X size={12} /> Annulla
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      padding: '6px 14px', borderRadius: 8,
                      border: 'none', background: '#013344', color: '#fff',
                      fontSize: 12, fontWeight: 700, cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      fontFamily: 'Satoshi, sans-serif', opacity: isSubmitting ? 0.7 : 1,
                    }}
                  >
                    <Save size={12} /> Salva
                  </button>
                </div>
              </form>
            </FormProvider>
          ) : (
            <>
              {/* Sezione anamnesi medica base */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={fieldStyle}>
                  <span style={labelStyle}>Condizioni croniche / Note generali</span>
                  {anamnesis?.generalNotes
                    ? <span style={valueStyle}>{anamnesis.generalNotes}</span>
                    : <span style={emptyStyle}>Non specificato</span>}
                </div>
                <div style={fieldStyle}>
                  <span style={labelStyle}>Allergie</span>
                  {anamnesis?.allergiesNotes
                    ? <span style={valueStyle}>{anamnesis.allergiesNotes}</span>
                    : <span style={emptyStyle}>Non specificato</span>}
                </div>
                <div style={fieldStyle}>
                  <span style={labelStyle}>Anamnesi familiare / Note psicologiche</span>
                  {anamnesis?.psychologicalNotes
                    ? <span style={valueStyle}>{anamnesis.psychologicalNotes}</span>
                    : <span style={emptyStyle}>Non specificato</span>}
                </div>
              </div>

              {/* Sezione documenti medici */}
              <div style={sectionTitleStyle}>Documenti medici</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={fieldStyle}>
                  <span style={labelStyle}>Ultima visita medica</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={valueStyle}>{formatDate(anamnesis?.lastMedicalVisit)}</span>
                    {(() => {
                      const badge = getMedicalVisitBadge(anamnesis?.lastMedicalVisit);
                      return badge ? (
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '2px 7px',
                          borderRadius: 9999, background: badge.color + '1a',
                          color: badge.color, letterSpacing: '0.04em',
                        }}>
                          {badge.label}
                        </span>
                      ) : null;
                    })()}
                  </div>
                </div>
                <div style={fieldStyle}>
                  <span style={labelStyle}>Scadenza certificato medico</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={valueStyle}>{formatDate(anamnesis?.medicalCertificateExpiry)}</span>
                    {(() => {
                      const badge = getCertificateBadge(anamnesis?.medicalCertificateExpiry);
                      return badge ? (
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '2px 7px',
                          borderRadius: 9999, background: badge.color + '1a',
                          color: badge.color, letterSpacing: '0.04em',
                        }}>
                          {badge.label}
                        </span>
                      ) : null;
                    })()}
                  </div>
                </div>
                <div style={{ ...fieldStyle, gridColumn: '1/-1' }}>
                  <span style={labelStyle}>Contatto emergenza</span>
                  {anamnesis?.emergencyContactName || anamnesis?.emergencyContactPhone ? (
                    <span style={valueStyle}>
                      {[anamnesis.emergencyContactName, anamnesis.emergencyContactPhone]
                        .filter(Boolean)
                        .join(' — ')}
                    </span>
                  ) : (
                    <span style={emptyStyle}>Non specificato</span>
                  )}
                </div>
              </div>

              {/* Sezione note cliniche */}
              <div style={sectionTitleStyle}>Note cliniche</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={fieldStyle}>
                  <span style={labelStyle}>Note fisioterapista</span>
                  {anamnesis?.physiotherapistNotes
                    ? <span style={valueStyle}>{anamnesis.physiotherapistNotes}</span>
                    : <span style={emptyStyle}>Non specificato</span>}
                </div>
                <div style={fieldStyle}>
                  <span style={labelStyle}>Infortuni pre-LEOPART</span>
                  {anamnesis?.preExistingInjuries
                    ? <span style={valueStyle}>{anamnesis.preExistingInjuries}</span>
                    : <span style={emptyStyle}>Non specificato</span>}
                </div>
              </div>

              {anamnesis?.updatedAt && (
                <div style={{ marginTop: 4 }}>
                  <span style={{ ...labelStyle, fontSize: 10, color: 'var(--c-text-3)' }}>
                    Ultimo aggiornamento: {new Date(anamnesis.updatedAt).toLocaleDateString('it-IT')}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AnamnesisCard;
